import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useTabbar } from '@common/providers/useTabbar';
import { tokenService } from '@common/services/tokenStore';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { CachedImage } from '@common/ui/CachedImage';
import { Loader } from '@common/ui/Loader';
import { getApiErrorInfo } from '@common/utils/fetcher';
import {
  finishInvite,
  markInviteOpened,
  readPendingInvite,
} from '@common/utils/startParam';

import { useInviteIntroStore } from '@entities/matchmaker';

import { analytics } from '@services/AnalyticsService';
import { usePostMatchmakerInviteOpen } from '@services/hooks/MatchmakerService';
import {
  MATCHMAKER_ERROR_CODES,
  type PostMatchmakerInviteOpenResponse,
} from '@services/MatchmakerService';

import { MatchmakerResult } from '@widgets/matchmaker/components/MatchmakerResult';

import styles from './InviteIntro.module.scss';

type IntroView =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | {
      kind: 'register_first';
      matchmakerName: string | null;
      note: string | null;
    }
  | { kind: 'closed'; reason: ClosedReason };

type ClosedReason = 'expired' | 'taken' | 'own_invite' | 'not_found';

const CLOSED_COPY: Record<
  ClosedReason,
  { testId: string; title: string; description: string; icon3d: string }
> = {
  expired: {
    testId: 'invite-intro-expired',
    title: 'Ссылка устарела',
    description: 'Она работает 7 дней. Попросите друга прислать новую',
    icon3d: 'sand-clock.png',
  },
  taken: {
    testId: 'invite-intro-taken',
    title: 'Ссылку уже открыли',
    description: 'Она работает только для одного человека',
    icon3d: 'sad.png',
  },
  own_invite: {
    testId: 'invite-intro-own-invite',
    title: 'Это ваша ссылка',
    description: 'Отправьте её другу — анкета откроется у него',
    icon3d: 'message.png',
  },
  not_found: {
    testId: 'invite-intro-not-found',
    title: 'Ссылка не работает',
    description: 'Возможно, она больше не действует',
    icon3d: 'not-found.png',
  },
};

const isClosedReason = (state: string): state is ClosedReason =>
  Object.prototype.hasOwnProperty.call(CLOSED_COPY, state);

/** Where the app goes on when the invite has nothing to show. */
const normalRoute = () => (tokenService.hasToken() ? '/dating' : '/sign-up');

/**
 * `/invite` (plan §4, board 4.2, reviews B1/S6): every pending invite is
 * opened here first, registered or not. The token never leaves storage and
 * the request body: no route, search param or analytics goal carries it.
 */
export const InviteIntro = () => {
  const navigate = useNavigate();
  const tabbar = useTabbar();
  // The slot's setter, without making it an effect dependency.
  const tabbarRef = useRef(tabbar);
  tabbarRef.current = tabbar;
  const rememberIntro = useInviteIntroStore((state) => state.rememberIntro);

  const [invite] = useState(() => readPendingInvite());
  const [view, setView] = useState<IntroView>(
    invite ? { kind: 'loading' } : { kind: 'closed', reason: 'not_found' },
  );
  const isOpenedRef = useRef(false);

  const { trigger: triggerPostMatchmakerInviteOpen } =
    usePostMatchmakerInviteOpen();

  const handleOpened = (
    token: string,
    { state, matchmaker, note }: PostMatchmakerInviteOpenResponse,
  ) => {
    analytics.trackMmInviteOpened(state);

    if (state === 'register_first' || state === 'registered') {
      markInviteOpened(token);
      rememberIntro({
        token,
        matchmakerName: matchmaker?.name ?? null,
        note,
      });

      if (state === 'registered') {
        navigate({ to: '/invite/candidate', replace: true });

        return;
      }

      setView({
        kind: 'register_first',
        matchmakerName: matchmaker?.name ?? null,
        note,
      });

      return;
    }

    finishInvite(token);
    // A state this client does not know (a newer server) reads as a dead link.
    setView({
      kind: 'closed',
      reason: isClosedReason(state) ? state : 'not_found',
    });
  };

  const openInvite = async () => {
    if (!invite) return;

    setView({ kind: 'loading' });

    try {
      const response = await triggerPostMatchmakerInviteOpen({
        token: invite.token,
      });

      handleOpened(invite.token, response);
    } catch (error) {
      const { status, code } = getApiErrorInfo(error);

      // The inviter's matchmaker feature is off: the invite is gone for good.
      // It says nothing about this user's own features.
      if (status === 404 && code === MATCHMAKER_ERROR_CODES.featureDisabled) {
        analytics.trackMmInviteOpened('feature_disabled');
        finishInvite(invite.token);
        setView({ kind: 'closed', reason: 'not_found' });

        return;
      }

      setView({ kind: 'failed' });
    }
  };

  useEffect(() => {
    if (isOpenedRef.current) return;

    isOpenedRef.current = true;
    void openInvite();
  }, []);

  useEffect(() => {
    if (view.kind !== 'register_first') {
      tabbarRef.current.setBottomSheet(undefined);

      return;
    }

    tabbarRef.current.setBottomSheet(
      <BottomSheet
        buttons={[
          <Button
            testId="invite-intro-sign-up-button"
            variant="actionPrimary"
            onClick={() => navigate({ to: '/sign-up' })}
          >
            Зарегистрироваться и посмотреть
          </Button>,
        ]}
      />,
    );
  }, [view.kind, navigate]);

  useEffect(() => () => tabbarRef.current.setBottomSheet(undefined), []);

  const renderView = () => {
    switch (view.kind) {
      case 'loading':
        return <Loader />;
      case 'failed':
        return (
          <MatchmakerResult
            testId="invite-intro-failed"
            icon3d="sad.png"
            title="Ошибка подключения"
            description="Не удалось подключиться к серверу"
            button={
              <div className={styles.failedButtons}>
                <Button
                  testId="invite-intro-retry-button"
                  onClick={() => void openInvite()}
                >
                  Повторить
                </Button>
                {/* The token stays: the next launch tries the invite again. */}
                <Button
                  testId="invite-intro-failed-continue-button"
                  variant="actionSecondary"
                  onClick={() => navigate({ to: normalRoute(), replace: true })}
                >
                  Продолжить
                </Button>
              </div>
            }
          />
        );
      case 'closed': {
        const copy = CLOSED_COPY[view.reason];

        return (
          <MatchmakerResult
            testId={copy.testId}
            icon3d={copy.icon3d}
            title={copy.title}
            description={copy.description}
            button={
              <Button
                testId="invite-intro-continue-button"
                onClick={() => navigate({ to: normalRoute(), replace: true })}
              >
                Продолжить
              </Button>
            }
          />
        );
      }
      case 'register_first':
        return (
          <div className={styles.intro} data-test-id="invite-intro-register">
            <div className={styles.content}>
              <div className={styles.tile}>
                <CachedImage
                  testId="invite-intro-heart"
                  src="/icons/3d-icons/likes.png"
                  alt=""
                  className={styles.tileImage}
                />
              </div>
              <h1 className={styles.title} data-test-id="invite-intro-title">
                {view.matchmakerName ? (
                  <>
                    <span className="ym-hide-content">
                      {view.matchmakerName}
                    </span>{' '}
                    советует тебе анкету в Kiss
                  </>
                ) : (
                  'Тебе советуют анкету в Kiss'
                )}
              </h1>
              <p className={styles.text}>
                Анкета откроется сразу после регистрации — это займёт пару минут
              </p>
              {view.note && (
                <figure
                  className={styles.note}
                  data-test-id="invite-intro-note"
                >
                  <figcaption className={styles.noteCaption}>
                    {view.matchmakerName ? (
                      <>
                        <span className="ym-hide-content">
                          {view.matchmakerName}
                        </span>{' '}
                        пишет:
                      </>
                    ) : (
                      'Записка'
                    )}
                  </figcaption>
                  <blockquote className={`${styles.noteText} ym-hide-content`}>
                    «{view.note}»
                  </blockquote>
                </figure>
              )}
            </div>
            <p className={styles.footnote}>
              Ссылка работает 7 дней и только для тебя
            </p>
          </div>
        );
    }
  };

  return (
    <div className={styles.inviteIntro} data-test-id="invite-intro">
      {renderView()}
    </div>
  );
};
